import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import adminService from "../../services/adminService";
import AdminDashboard from "../../components/admin/AdminDashboard";
import AdminModelManager from "../../components/admin/AdminModelManager";
import CircuitSurMesureManager from "../../components/admin/CircuitSurMesureManager";
import InvoiceRequestManager from "../../components/admin/InvoiceRequestManager";
import AgencyPartnershipAdmin from "../../components/admin/AgencyPartnershipAdmin";
import ReviewManager from "../../components/admin/ReviewManager";
import PaymentsManager from "../../components/admin/PaymentsManager";
import CircuitReservationsManager from "../../components/admin/CircuitReservationsManager";
import ReservationsByServiceManager from "../../components/admin/ReservationsByServiceManager";
import SettingsManager from "../../components/admin/SettingsManager";

/**
 * ✅ Ajout "Commentaires / Testimonials" dans l'admin sidebar.
 * - On ajoute un tab fixe: id = "commentaires"
 * - AdminModelManager a déjà été modifié pour supporter ce modelName (commentaires/testimonials)
 * - Pas besoin que le backend le renvoie via getAvailableModels (sinon on va le dupliquer)
 */

const AdminPanel = () => {
  const { currentUser, isAdmin } = useAuth();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (typeof isAdmin !== "function") {
      setError("Erreur d'authentification");
      setLoading(false);
      return;
    }

    if (!isAdmin()) {
      setLoading(false);
      return;
    }

    const loadModels = async () => {
      try {
        const response = await adminService.getAvailableModels();
        setModels(response.data || []);
      } catch (error) {
        console.error("Erreur lors du chargement des modèles:", error);
        setError("Erreur lors du chargement des modèles: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadModels();
  }, [isAdmin]);

  // ✅ Tab fixe Commentaires (pour activer/désactiver ceux affichés sur Home)
  const fixedTabs = useMemo(
    () => [
      { id: "dashboard", name: "Tableau de Bord", icon: "📊" },
      { id: "payments", name: "Paiements", icon: "💳" },
      { id: "invoiceRequests", name: "Demandes de Factures", icon: "📄" },
      { id: "circuitsSurMesure", name: "Circuits Sur Mesure", icon: "✨" },
      { id: "agencyPartnerships", name: "Partenariats d'Agences", icon: "🤝" },
      { id: "reviews", name: "Gestion des Avis", icon: "⭐" },
      // ✅ NOUVEAU : gestion testimonials (status active/inactive + upload photo cloudinary)
      { id: "commentaires", name: "Témoignages (Home)", icon: "💬" },
      { id: "settings", name: "Paramètres", icon: "⚙️" },
    ],
    []
  );

  // ✅ Evite doublons si ton backend renvoie déjà un modèle "commentaires"
  const dynamicTabs = useMemo(() => {
    const normalizedFixed = new Set(fixedTabs.map((t) => String(t.id).toLowerCase()));

    return (models || [])
      .filter((model) => model && model.key)
      .filter((model) => !normalizedFixed.has(String(model.key).toLowerCase()))
      .map((model) => ({
        id: model.key,
        name: model.name,
        icon: getModelIcon(model.key),
      }));
  }, [models, fixedTabs]);

  const tabs = useMemo(() => [...fixedTabs, ...dynamicTabs], [fixedTabs, dynamicTabs]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 pt-20">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Erreur</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (typeof isAdmin !== "function" || !isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 pt-20">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Accès Refusé</h2>
          <p className="text-slate-600">
            Vous n'avez pas les privilèges d'administrateur requis pour accéder à cette page.
          </p>
          <div className="mt-4 text-sm text-slate-500">
            <p>Utilisateur actuel: {currentUser ? currentUser.email || "Pas d'email" : "Non connecté"}</p>
            <p>Rôle: {currentUser ? currentUser.role || "Pas de rôle" : "Non défini"}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="flex flex-col lg:flex-row min-h-screen pt-20 lg:pt-20">
        {/* Sidebar - Mobile Dropdown */}
        <div className="lg:hidden">
          <div className="bg-white border-b border-slate-200 mt-4 mx-4 rounded-lg shadow-sm">
            <div className="px-4 py-3">
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.icon} {tab.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block lg:w-72 lg:flex-shrink-0 lg:pl-6">
          <div className="sticky top-24 bg-white shadow-xl border border-slate-100 rounded-3xl overflow-hidden">
            {/* Brand */}
            <div className="relative px-5 py-5 text-white overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-700" />
              <div className="relative">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300/90">Palais Touristique</p>
                <p className="text-lg font-black mt-0.5 flex items-center gap-2">⚙️ Administration</p>
              </div>
            </div>
            <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-230px)]">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-2xl transition-all duration-200 flex items-center gap-3 group ${
                      active
                        ? "bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg shadow-orange-200"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <span className={`w-9 h-9 flex-none rounded-xl flex items-center justify-center text-base transition-transform group-hover:scale-110 ${active ? "bg-white/20" : "bg-slate-100 group-hover:bg-orange-50"}`}>
                      {tab.icon}
                    </span>
                    <span className={`font-semibold text-sm leading-tight flex-1 truncate ${active ? "text-white" : "text-slate-700 group-hover:text-orange-600"}`}>
                      {tab.name}
                    </span>
                    {active && <span className="w-1.5 h-6 rounded-full bg-white/80 flex-none" />}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-hidden">
          <div className="max-w-full mt-4">
            {activeTab === "dashboard" ? (
              <AdminDashboard onTabChange={setActiveTab} />
            ) : activeTab === "payments" ? (
              <PaymentsManager />
            ) : activeTab === "invoiceRequests" ? (
              <InvoiceRequestManager />
            ) : activeTab === "circuitsSurMesure" ? (
              <CircuitSurMesureManager />
            ) : activeTab === "agencyPartnerships" ? (
              <AgencyPartnershipAdmin />
            ) : activeTab === "reviews" ? (
              <ReviewManager />
            ) : activeTab === "settings" ? (
              <SettingsManager />
            ) : activeTab === "reservationCircuits" ? (
              <CircuitReservationsManager />
            ) : ["reservationHotels", "reservationTransports", "reservationVisites", "reservationVoitures"].includes(activeTab) ? (
              <ReservationsByServiceManager modelName={activeTab} />
            ) : (
              // ✅ Ici "commentaires" tombera dans AdminModelManager
              <AdminModelManager modelName={activeTab} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Fonction pour obtenir l'icône d'un modèle
const getModelIcon = (modelKey) => {
  const icons = {
    hotels: "🏨",
    circuits: "🗺️",
    transports: "🚌",
    visites: "👁️",
    voitures: "🚗",
    priseEnCharge: "🤝",
    comptes: "👥",
    reservationHotels: "📋",
    reservationCircuits: "📝",
    reservationTransports: "🎫",
    reservationVisites: "🎟️",
    reservationVoitures: "🔖",
    commentaires: "💬",
    testimonials: "💬",
    temoignages: "💬",
  };
  return icons[modelKey] || "📦";
};

export default AdminPanel;
